import i18n from '@/i18n';
import router from '@/router';
import cloneDeep from 'lodash/cloneDeep';
import { decodeMenus, decodeOrder, decodeString } from '@/utils/decoding';
import formatOrder, { formatOrderItem } from '@/utils/format';
import { isPizzabotIntegration } from '@/utils/general';
import { AxiosInstance } from 'axios';
import { DateTime } from 'luxon';
import { ActionTree } from 'vuex';
import { handleAllErrors } from '../../utils/errorHandling';
import { state as authState } from '../auth/auth';
import { OrdersState, RootState } from '../types';
import { state } from './orders';

/**
 * Keep only the options a POS-started check selected, out of the item's own option groups. A check
 * sends the skus the guest picked but not the parents they sit under, so an unsent option is kept
 * when a sku under it matched. Sent options match first, as a sku can repeat elsewhere in the item.
 *
 * @param {OrderOptionGroup[]} optionGroups - the item's option groups, as built by formatOrderItem
 * @param {Map<string, number>} pending - selected option skus and quantities, consumed as matched
 * @return {OrderOptionGroup[]}
 */
function selectPosCheckOptionGroups(optionGroups: OrderOptionGroup[], pending: Map<string, number>): OrderOptionGroup[] {
	const selectedGroups: OrderOptionGroup[] = [];

	for (const optionGroup of optionGroups) {
		const values: (OrderOption | undefined)[] = [];

		optionGroup.values.forEach((option: OrderOption, index: number) => {
			const quantity: number | undefined = option.sku ? pending.get(option.sku) : undefined;
			if (!option.sku || !quantity) {
				return;
			}

			pending.delete(option.sku);
			values[index] = { ...option, quantity, options: selectPosCheckOptionGroups(option.options || [], pending) };
		});

		optionGroup.values.forEach((option: OrderOption, index: number) => {
			if (values[index]) {
				return;
			}

			const options: OrderOptionGroup[] = selectPosCheckOptionGroups(option.options || [], pending);
			if (options.length) {
				values[index] = { ...option, quantity: 1, options };
			}
		});

		const selectedValues: OrderOption[] = values.filter((value): value is OrderOption => !!value);
		if (selectedValues.length) {
			selectedGroups.push({ ...optionGroup, values: selectedValues });
		}
	}

	return selectedGroups;
}

/**
 * Find a menu item by its id, along with the section it sits in, so it can be
 * added to the cart the same way the item viewer does.
 *
 * @param {Menu[]} menus - the menus to search
 * @param {number} itemId - the item to look for
 * @return {{ item: MenuItem; sectionSlug: string } | undefined}
 */
function findMenuItem(menus: Menu[], itemId: number): { item: MenuItem; sectionSlug: string } | undefined {
	for (const menu of menus) {
		const item: MenuItem | undefined = (menu.items || []).find((menuItem: MenuItem) => menuItem.id === itemId);
		if (item) {
			return { item: { ...item, menu_id: menu.id! }, sectionSlug: '' };
		}

		for (const section of menu.sections || []) {
			const sectionItem: MenuItem | undefined = (section.items || []).find((menuItem: MenuItem) => menuItem.id === itemId);
			if (sectionItem) {
				return { item: { ...sectionItem, menu_id: menu.id! }, sectionSlug: section.slug };
			}
		}
	}
}

/**
 * Create an open item in its menu and section, and format it as an order item ready for the cart.
 *
 * @param {MenuItem} item - the open item's name, price, description, menu, section, category and tax rate
 * @param {boolean} saveOpenItem - whether the item is kept once the order is submitted
 * @return {Promise<MenuOrderItem>}
 */
async function createOpenOrderItem(item: MenuItem, saveOpenItem: boolean): Promise<MenuOrderItem> {
	const axiosInst: AxiosInstance = authState.axiosInst;
	try {
		const { data } = await axiosInst.post('/menus/open-items', {
			name: item.name,
			price: item.price,
			description: item.description,
			menu_id: item.menu_id,
			section_id: item.section_id,
			category: item.category || undefined,
			tax_rate: item.tax_rate || undefined,
			save: saveOpenItem
		});

		const orderItem = formatOrderItem({
			...data.item,
			name: item.name,
			menu_id: data.menu_id,
			section_id: data.section_id,
			options: []
		});
		orderItem.openItem = true;
		orderItem.save_open_item = saveOpenItem;

		return orderItem;
	}
	catch (error) {
		throw handleAllErrors(error);
	}
}

/**
 * Names of the options an item was ordered with that today's menu no longer offers. Groups and
 * options are matched by id, and the sub-options of the options that are still offered are walked
 * the same way.
 *
 * @param {OrderOptionGroup[]} sourceGroups - the option groups as they were ordered
 * @param {OrderOptionGroup[]} liveGroups - the same option groups on today's menu
 * @return {string[]}
 */
function getRemovedModifiers(sourceGroups: OrderOptionGroup[], liveGroups: OrderOptionGroup[]): string[] {
	return sourceGroups
		.filter((group: OrderOptionGroup) => !group.memo && !group.defaultPricingOption)
		.flatMap((sourceGroup: OrderOptionGroup) => {
			const liveGroup = liveGroups.find((group: OrderOptionGroup) => group.id === sourceGroup.id);

			return sourceGroup.values.flatMap((sourceValue: OrderOption) => {
				const liveValue = liveGroup?.values.find((value: OrderOption) => value.id === sourceValue.id);

				return liveValue ? getRemovedModifiers(sourceValue.options || [], liveValue.options || []) : [sourceValue.name];
			});
		});
}

/**
 * Rebuild the options an item was ordered with from the same option groups on today's menu, so
 * their names and prices are today's. Groups and options are matched by id and keep the quantities
 * they were ordered with. Special instructions are carried over as they were.
 *
 * @param {OrderOptionGroup[]} sourceGroups - the option groups as they were ordered
 * @param {OrderOptionGroup[]} liveGroups - the same option groups on today's menu
 * @return {OrderOptionGroup[]}
 */
function getLiveOrderOptions(sourceGroups: OrderOptionGroup[], liveGroups: OrderOptionGroup[]): OrderOptionGroup[] {
	return sourceGroups.flatMap((sourceGroup: OrderOptionGroup) => {
		if (sourceGroup.memo) {
			return [sourceGroup];
		}

		const liveGroup = liveGroups.find((group: OrderOptionGroup) => group.id === sourceGroup.id);
		if (!liveGroup) {
			return [];
		}

		const values: OrderOption[] = sourceGroup.values.flatMap((sourceValue: OrderOption) => {
			const liveValue = liveGroup.values.find((value: OrderOption) => value.id === sourceValue.id);

			return liveValue
				? [{ ...liveValue, quantity: sourceValue.quantity, options: getLiveOrderOptions(sourceValue.options || [], liveValue.options || []) }]
				: [];
		});

		return [{ ...liveGroup, values }];
	});
}

/**
 * Whether today's menu requires a choice the rebuilt options don't make. Every required group must
 * have a selection, and the sub-option groups of each selected option are checked the same way,
 * as the item viewer does before adding an item.
 *
 * @param {OrderOptionGroup[]} liveGroups - the option groups on today's menu
 * @param {OrderOptionGroup[]} orderGroups - the options rebuilt from the order being repeated
 * @return {boolean}
 */
function isMissingRequiredOption(liveGroups: OrderOptionGroup[], orderGroups: OrderOptionGroup[]): boolean {
	return liveGroups.some((liveGroup: OrderOptionGroup) => {
		const orderGroup: OrderOptionGroup | undefined = orderGroups.find((group: OrderOptionGroup) => group.id === liveGroup.id);
		const selectedOptions: OrderOption[] = (orderGroup?.values || []).filter((option: OrderOption) => option.quantity);
		if (!selectedOptions.length) {
			return !!liveGroup.requiredOption;
		}

		return selectedOptions.some((selectedOption: OrderOption) => {
			const liveOption: OrderOption | undefined = liveGroup.values.find((option: OrderOption) => option.id === selectedOption.id);

			return isMissingRequiredOption(liveOption?.options || [], selectedOption.options || []);
		});
	});
}

export const actions: ActionTree<OrdersState, RootState> = {

	/**
	 * Fetch orders for marketplace locations
	 *
	 * @param {string} [lastFetchDate] - The latest created_at value of the pending orders
	 * @return {Promise<{ areThereNewOrders: boolean, fetchDate: string } | undefined>} - Whether there are new orders or not, and at what time the fetch was made
	 */
	async fetchMarketplaceOrders({ commit }, lastFetchDate? : string): Promise<{ areThereNewOrders: boolean, fetchDate: string } | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data, headers } = await axiosInst.get<{ orders: Order[], newOrders: boolean }>('/orders/get-marketplace-orders', {
				params: { lastOrder: lastFetchDate }
			});
			commit('SET_CURRENT_ORDERS', data.orders);
			return { areThereNewOrders: data.newOrders, fetchDate: headers['date'] };
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch orders for pizzabot locations, grouped by parent + children
	 *
	 * @param {string} [lastFetchDate] - The latest created_at value of the pending orders
	 * @return {Promise<{ areThereNewOrders: boolean, fetchDate: string } | undefined>} - Whether there are new orders or not, and at what time the fetch was made
	 */
	async fetchPizzabotOrders({ commit }, lastFetchDate? : string): Promise<{ areThereNewOrders: boolean, fetchDate: string } | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data, headers } = await axiosInst.get<{ orders: Order[], newOrders: boolean }>('/orders/get-pizzabot-orders', {
				params: { lastOrder: lastFetchDate }
			});
			commit('SET_CURRENT_ORDERS', data.orders);
			return { areThereNewOrders: data.newOrders, fetchDate: headers['date'] };
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch current orders with filter parameters
	 * * If categorized is true, we categorized between pending, in-progress, and completed/refunded and set the current orders
	 *
	 * @param {OrderFilterParams} filters - The filters to apply to the orders
	 * @return {Promise<{ orders: Order[], areThereNewOrders: boolean, fetchDate: string, statusTotals?: { pending: number; inProgress: number; completed: number }, totalCount?: number } | undefined>} - Orders, Whether there are new orders or not, and at what time the fetch was made
	 */
	async fetchOrders({ commit }, filters: OrderFilterParams & { commitToStore?: boolean } = {}): Promise<{ orders: Order[], areThereNewOrders: boolean, fetchDate: string, statusTotals?: { pending: number; inProgress: number; completed: number }, totalCount?: number } | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { commitToStore = true, ...ordersFilters } = filters;
			const { data, headers } = await axiosInst.get<{ orders: Order[], newOrders: boolean, statusTotals?: { pending: number; inProgress: number; completed: number }, totalCount?: number }>('/orders', {
				params: ordersFilters
			});

			if (commitToStore) {
				commit('SET_CURRENT_ORDERS', data.orders);
			}
			return { orders: data.orders, areThereNewOrders: data.newOrders, fetchDate: headers['date'], statusTotals: data.statusTotals, totalCount: data.totalCount };
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async fetchOrderById({}, payload: { orderId: string | number, groupItems?: boolean }): Promise<Order | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<Order>(`/orders/${payload.orderId}`, {
				params: {
					includeModificationRequests: !!(authState.restaurant.catering && authState.restaurant.modify_order_requests),
					...(payload.groupItems && { groupItems: true })
				}
			});
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch cancelled orders in the last 2 days
	 *
	 * @return {Promise<Order[] | undefined>}
	 */
	async fetchCancelledOrders(): Promise<Order[] | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<{ newOrders: boolean; orders: Order[]}>('/orders', {
				params: { status: JSON.stringify(['cancelled', 'refunded', 'refund-requested', 'partially-refunded', 'partial-refund-requested']) }
			});
			return data.orders;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch an order by id and then set orders state with the order info
	 *
	 * @param {string|number} orderId
	 * @return {Promise<void>}
	 */
	async fetchAndSetOrderInfo({ commit, dispatch }, orderId: string | number): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<{ order: Order; errors: string[] }>(`/orders/order-with-validated-items/${orderId}`);
			commit('SET_ORDER_INFO', decodeOrder(data.order));
			dispatch('setCosts');

			if (data.errors && Object.keys(data.errors).length) {
				commit('SET_ORDER_VALIDATION_ERRORS', data.errors);
			}
		}
		catch (error) {
			router.push('/order-management');
			handleAllErrors(error);
		}
	},

	/**
	 * Load a previous order into the create order state so it can be repeated. The checkout details
	 * are reused as they were, while the order type the operator just chose is kept, and the cart is
	 * rebuilt from the live menus item by item, the same way the item viewer adds one, so every item
	 * and every option it was ordered with carries today's name and price. The items are validated
	 * against the chosen order type in a single call rather than one call per item, and the costs are
	 * set once at the end. Unsaved open items are created again, unsaved, in the menu and section they
	 * were ordered from, at the price they were ordered at, while saved ones are repeated like any other
	 * item, looked up in the unpublished menus too as the Uncategorized menu is never published. Items
	 * the location can no longer serve, including those that lost one of the options they were ordered
	 * with or that the validation rejected, are left out and reported for the cart banners.
	 *
	 * @param {ActionContext} context - vuex action context
	 * @param {string | number} orderId - the order being repeated
	 * @return {Promise<void>}
	 */
	async repeatOrder({ commit, dispatch, rootGetters }, orderId: string | number): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const [{ data }, { data: validation }] = await Promise.all([
				axiosInst.get<Order>(`/orders/${orderId}`),
				axiosInst.get<{ rejectedItemIndexes?: number[] }>(`/orders/order-with-validated-items/${orderId}`, {
					params: { type: state.orderDetails.type, date: DateTime.local().toISO() }
				})
			]);
			const sourceOrder: Order = decodeOrder(data);
			const rejectedItemIndexes: Set<number> = new Set(validation.rejectedItemIndexes || []);
			const publishedMenus: Menu[] = rootGetters['menus/getPublishedMenus'];
			const allMenus: Menu[] = sourceOrder.data.items.some((item: OrderItem) => item.save_open_item) ? decodeMenus(rootGetters['menus/getMenus'], true) : [];
			const notices: RepeatOrderNotices = { removed: [], removedModifiers: [], failedToAdd: [], repriced: [] };
			const chosenOrderDetails: OrderDetails = { ...state.orderDetails };

			commit('SET_ORDER_INFO', sourceOrder);
			commit('SET_ORDER_DETAILS', chosenOrderDetails);
			commit('CLEAR_CART');
			commit('SET_REPEAT_ORDER_INFO', { removed: [], removedModifiers: [], failedToAdd: [], repriced: [] });

			let addedOpenItem: boolean = false;
			for (const [itemIndex, sourceItem] of sourceOrder.data.items.entries()) {
				if (sourceItem.openItem && !sourceItem.save_open_item) {
					try {
						const openItem: MenuOrderItem = await createOpenOrderItem({ ...sourceItem, price: String(sourceItem.price || 0) }, false);
						commit('SET_ORDER_ITEM', { selectedItem: openItem, formatItem: false });
						commit('SET_ORDER_ITEM_QUANTITY', sourceItem.quantity || 1);
						commit('ADD_TO_CART', '');
						commit('RESET_ITEM_VIEWER_STATE');
						addedOpenItem = true;
					}
					catch (error) {
						if (typeof error !== 'string' || [i18n.t('shared.error_timed_out'), i18n.t('shared.error_tenant_not_recognized')].includes(error)) {
							throw error;
						}
						notices.failedToAdd.push(sourceItem.name);
					}
					continue;
				}

				const match = findMenuItem(publishedMenus, sourceItem.id)
					|| (sourceItem.save_open_item ? findMenuItem(allMenus, sourceItem.id) : undefined);
				const liveOptions: OrderOptionGroup[] = match ? formatOrderItem(match.item).orderOptions || [] : [];
				const missingModifiers: string[] = getRemovedModifiers(sourceItem.orderOptions || [], liveOptions);
				const liveOrderOptions: OrderOptionGroup[] = getLiveOrderOptions(sourceItem.orderOptions || [], liveOptions);
				const missingRequiredOption: boolean = isMissingRequiredOption(liveOptions, liveOrderOptions);

				// No addToCart needed: Remy already validated every item above (rejectedItemIndexes).
				// Remy doesn't check required option groups or sub-option groups, so that's done here.
				if (!match || missingModifiers.length || missingRequiredOption || rejectedItemIndexes.has(itemIndex)) {
					if (missingModifiers.length) {
						notices.removedModifiers.push(`${missingModifiers.join(', ')} (${sourceItem.name})`);
					}
					else if (match) {
						notices.failedToAdd.push(sourceItem.name);
					}
					else {
						notices.removed.push(sourceItem.name);
					}
					continue;
				}

				const quantity: number = sourceItem.quantity || 1;
				dispatch('setSelectedItem', { selectedItem: match.item, sectionSlug: match.sectionSlug });
				dispatch('setOrderItemOptions', liveOrderOptions);
				dispatch('setOrderItemQuantity', quantity);
				const oldPrice: number = Number(sourceItem.orderPrice) / quantity;
				const newPrice: number = Number(state.orderItem?.orderPrice) / quantity;
				commit('ADD_TO_CART', '');
				commit('RESET_ITEM_VIEWER_STATE');

				if (typeof sourceItem.orderPrice === 'number' && oldPrice !== newPrice && !notices.repriced.some((change: RepeatOrderPriceChange) => change.name === match.item.name)) {
					notices.repriced.push({ name: match.item.name, oldPrice, newPrice });
				}
			}

			commit('RESET_ITEM_VIEWER_STATE');
			commit('SET_REPEAT_ORDER_INFO', {
				...notices,
				removed: Array.from(new Set(notices.removed)),
				removedModifiers: Array.from(new Set(notices.removedModifiers)),
				failedToAdd: Array.from(new Set(notices.failedToAdd))
			});
			await dispatch('setCosts');
			if (addedOpenItem) {
				await dispatch('menus/fetchMenus', {}, { root: true });
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch orders ids only, categorized by status.
	 * For customer-facing display screen.
	 *
	 * @return {Record<string, string[]> | void}
	 */
	async fetchOrdersForCustomerDisplay(): Promise<Record<string, string[]> | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<Record<string, string[]>>('/orders/customer-facing');
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Send a deposit request
	 *
	 * @param {UpdateOrderPayload} payload
	 * @return {Promise<void>}
	 */
	async sendDepositRequest({}, payload: RequestDepositPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.post<Order>('/orders/request-deposit', payload, {
				headers: {
					'Authorization': authState.idToken as string
				}
			});
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Update an order
	 *
	 * @param {UpdateOrderPayload} payload
	 * @return {Promise<void>}
	 */
	async updateOrder({}, { orderId, payload }: UpdateOrderPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.put<Order>(`/orders/${orderId}`, payload);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Modify the delivery information for a school order
	 * This is only supported for in-progress school orders at the moment
	 * If modifyAllOrders is true, the delivery information will be modified for all orders submitted together
	 *
	 * @param {ModifySchoolDeliveryPayload} payload
	 * @return {Promise<void>}
	 */
	async modifySchoolDeliveryInfo({}, { id, delivery, modifyAllOrders }: ModifySchoolDeliveryPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.put<Order>(`/orders/modify-school-delivery/${id}`, { delivery, modifyAllOrders });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Refund an order
	 * For a lead, Salata sends a refund request to support instead of refunding
	 *
	 * @param {RefundOrderPayload} payload
	 * @return {Promise<void>}
	 */
	async refundOrder({}, { orderId, cancellationNote }: RefundOrderPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.post<any>('/orders/refund', {
				orderId,
				app8RestaurantId: authState.restaurant.app8_restaurant,
				cancellation_note: cancellationNote
			});
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Send an array of order ids to the bulk actions endpoint to perform an action on them
	 *
	 * @param {BulkActionPayload} payload - Contains array of order ids and the action to perform on them
	 * @return {Promise<BulkActionResponse | void>}
	 */
	async bulkActions({}, payload: BulkActionPayload): Promise<BulkActionResponse | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.post<BulkActionResponse>('/orders/bulk-actions', payload);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async fetchOrderModificationRequests({}, orderId: string): Promise<OrderModificationRequest[] | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<OrderModificationRequest[]>(`/modify-requests?orderId=${orderId}`);
			return data.map(request => ({
				...request,
				request_details: decodeString(request.request_details)
			}));
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async fetchOrderModificationRequestById({}, modificationRequestId: number): Promise<OrderModificationRequest | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<OrderModificationRequest>(`/modify-requests/${modificationRequestId}`);
			return {
				...data,
				request_details: decodeString(data.request_details)
			};
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async updateOrderModificationRequest({ commit }, payload: OrderModificationRequest): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.put<void>(`/modify-requests/${payload.id}`, payload);

			// If the updated request is the active one, clear it since its status has changed
			if (state.activeModificationRequest && state.activeModificationRequest.id === payload.id) {
				commit('SET_ACTIVE_MODIFICATION_REQUEST', null);
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	setActiveModificationRequest({ commit }, modificationRequest: OrderModificationRequest | null): void {
		commit('SET_ACTIVE_MODIFICATION_REQUEST', modificationRequest);
	},

	/**
	 * Get the costs of the order
	 *
	 * @param {Order} order
	 * @return {Promise<{ orderItems: OrderItem[], costs: Costs } | undefined>}
	 */
	async getOrderCosts({}, order: Order): Promise<{ orderItems: OrderItem[], costs: Costs } | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.post<{ orderItems: OrderItem[], costs: Costs }>('/orders/recalculate-costs', order);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	setModifyingOrder({ commit }, modifyingOrder: boolean): void {
		commit('SET_MODIFYING_ORDER', modifyingOrder);
	},

	setIsQuoting({ commit }, isQuoting: boolean): void {
		commit('SET_IS_QUOTING', isQuoting);
	},

	setEditingItem({ commit }, editingItem: boolean): void {
		commit('SET_EDITING_ITEM', editingItem);
	},

	setOrderDetails({ commit }, orderDetails: OrderDetails): void {
		commit('SET_ORDER_DETAILS', orderDetails);
	},

	resetOrderDetails({ commit }): void {
		commit('SET_ORDER_DETAILS', {
			type: '',
			tableArea: '',
			tableLocation: '',
			tableNum: '',
			takeoutType: '',
			checkNum: '',
			menuManagerEvent: undefined,
			menuManagerEventBEO: undefined,
			menuManagerEventPackage: undefined,
			menuManagerLaneReservation: undefined
		});
	},

	setSelectedItem({ commit }, { selectedItem, sectionSlug }: { selectedItem: MenuItem; sectionSlug?: string }): void {
		commit('SET_SELECTED_ITEM', selectedItem);
		if (sectionSlug) {
			commit('SET_SELECTED_SECTION_SLUG', sectionSlug);
		}
		else {
			// Clear section slug when no section is provided (e.g., menu without sections)
			commit('SET_SELECTED_SECTION_SLUG', '');
		}
		commit('SET_ORDER_ITEM', { selectedItem, formatItem: true });
	},

	setOrderItem({ commit }, orderItem: OrderItem): void {
		commit('SET_ORDER_ITEM', { selectedItem: orderItem, formatItem: false });
	},

	/**
	 * Set the order item from a menu item without triggering the item viewer
	 * Used for adding items directly to cart (e.g., checkin questions)
	 *
	 * @param {MenuItem} menuItem
	 * @return {void}
	 */
	setOrderItemFromMenuItem({ commit }, menuItem: MenuItem): void {
		commit('SET_ORDER_ITEM', { selectedItem: menuItem, formatItem: true });
	},

	setOrderItemOptions({ commit }, orderItemOptions: OrderOptionGroup[]): void {
		commit('SET_ORDER_ITEM_OPTIONS', orderItemOptions);
	},

	setOrderItemQuantity({ commit }, quantity: number): void {
		commit('SET_ORDER_ITEM_QUANTITY', quantity);
	},

	async addToCart({ commit, dispatch }, specialInstructions: string): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		const itemPayload = {
			itemsInCart: state.cart.items,
			itemToAdd: state.orderItem,
			type: state.orderDetails.type,
			date: DateTime.local().toISO(),
			isMenuManagerOrder: true,
			isKiosk: authState.role === 'kiosk'
		};

		// Create a snapshot of the cart and order item so the change can be reverted if an error occurs when updating cart
		const previousCart: Cart = cloneDeep(state.cart);
		const previousOrderItem: MenuOrderItem | null = cloneDeep(state.orderItem);
		try {
			await axiosInst.post('/orders/item', itemPayload);
			commit('ADD_TO_CART', specialInstructions);
			await dispatch('setCosts');
		}
		catch (error) {
			commit('SET_CART', previousCart);
			commit('SET_ORDER_ITEM', { selectedItem: previousOrderItem, formatItem: false });
			handleAllErrors(error);
		}
	},

	removeFromOrder({ commit, dispatch }, index: number): void {
		const removedItem = state.cart.items.find((item: MenuOrderItem) => item.cartIndex === index);
		commit('REMOVE_FROM_ORDER', index);
		dispatch('setCosts');
		commit('RESET_ITEM_VIEWER_STATE');

		// Unsaved open items only exist for this order, so delete the item when it's removed
		if (removedItem?.openItem && !removedItem.save_open_item && removedItem.id) {
			dispatch('deleteOpenItem', removedItem);
		}
	},

	async setCosts({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		// Determine menuManagerEvent based on all cart items:
		// - If any item is NOT replenish/extend (isReplenishExtend = false), then menuManagerEvent = false
		// - Only if ALL items are replenish/extend (isReplenishExtend = true), then menuManagerEvent = true
		const allItemsAreReplenishExtend = state.cart.items.length && state.cart.items.every((item: OrderItem) => item.isReplenishExtend);
		const menuManagerEventForCosts = state.orderDetails.menuManagerEvent && allItemsAreReplenishExtend;

		const payload = {
			tableNum: state.orderDetails.tableNum,
			items: state.cart.items,
			costs: state.costs,
			checkout: {
				...state.checkout,
				pickup: state.checkout.pickup,
				cateringEvent: state.checkout.cateringEvent?.eventStartDate && state.checkout.cateringEvent?.eventEndDate
					? state.checkout.cateringEvent
					: null,
				memberInfo: state.checkout.memberInfo ? {
					...state.checkout.memberInfo,
					promosApplied: state.checkout.memberInfo?.promosApplied ? Array.from(state.checkout.memberInfo.promosApplied) : null
				} : null
			},
			type: state.orderDetails.type,
			menuManagerEvent: state.orderDetails.menuManagerEvent,
			paidItemEvent: !menuManagerEventForCosts,
			menuManagerLaneReservation: state.orderDetails.menuManagerLaneReservation,
			modifyingOrder: state.modifyingOrder,
			orderId: state.selectedOrder?.id,
			date: DateTime.local().toISO()
		};

		try {
			const res = await axiosInst.post('/orders/set-costs', payload);
			commit('SET_COSTS', res.data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Validate the cart before going to the checkout process
	 *
	 * @return {Promise<void>}
	 */
	async validateCart({}): Promise<void> {
		if (state.cart.items) {
			const axiosInst: AxiosInstance = authState.axiosInst;
			const cartRequest = {
				itemsInCart: state.cart.items,
				type: state.orderDetails.type,
				date: state.selectedOrder?.data ? state.selectedOrder.data.date : DateTime.local().toISO(),
				isMenuManagerOrder: true,
				isKiosk: authState.role === 'kiosk'
			};
			try {
				await axiosInst.post('/orders/validate', cartRequest);
			}
			catch (error) {
				handleAllErrors(error);
			}
		}
	},

	async fetchSchedulingRestrictions({}, payload: MenuOrderItem[]): Promise<any> {
		try {
			const axiosInst: AxiosInstance = authState.axiosInst;
			const res = await axiosInst.post<ScheduleRestrictions>('/orders/scheduling', payload);
			return res.data;

		}
		catch (error) {
			throw error;
		}
	},

	async submitOrder({ commit }, inquiryId: number | null): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const ordersRequest: any = {
				type: state.orderDetails.type,
				data: formatOrder(state, authState),
				status: state.selectedOrder?.status ?? 'pending',
				inquiry_form_request_id: inquiryId
			};

			if (state.checkout.pickup && state.checkout.pickup.dueByDate) {
				const dueByDate: DateTime = DateTime.fromISO(state.checkout.pickup!.dueByDate);
				const dueByTime: DateTime | null = state.checkout.pickup.dueByTime ? DateTime.fromFormat(state.checkout.pickup.dueByTime!, i18n.t('orders.guest_details.takeout.timepicker_format')) : null;

				// JS Dates have months range from 0 - 11 instead of 1 - 12 like luxon
				ordersRequest.pickup_time = DateTime.fromObject({
					year: dueByDate.year,
					month: dueByDate.month,
					day: dueByDate.day,
					hour: dueByTime ? dueByTime.hour : 12,
					minute: dueByTime ? dueByTime.minute : undefined
				});

				// We add prep time to the pickup_time if ASAP
				if(!state.checkout.pickup.scheduled && state.checkout.pickup.prepTime) {
					ordersRequest.pickup_time = ordersRequest.pickup_time.plus({ minutes: state.checkout.pickup.prepTime }).toUTC().toISO();
				}

				// Scheduled is already adjusted with the correct time
				else {
					ordersRequest.pickup_time = ordersRequest.pickup_time.toUTC().toISO();
				}
			}
			if (state.modifyingOrder && state.selectedOrder?.id) {
				await axiosInst.put(`/orders/modify/${state.selectedOrder.id}`, ordersRequest);
			}
			else if (isPizzabotIntegration(authState.restaurant.integration_type)) {
				await axiosInst.post('/orders/create-pizzabot-order', ordersRequest);
				commit('CLEAR_CART');
			}
			else {
				await axiosInst.post('/orders', ordersRequest);
				commit('CLEAR_CART');
			}
			if(!!window.B2BApp) {
				window.B2BApp.orderSubmitted();
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Look up the membership profile for the given identifier across all of the
	 * location's enabled providers. The result (member + provider groups) is cached
	 * in the store so the checkout selector can rehydrate; applying a chosen provider
	 * is a separate step (applyPromoCode) once staff picks one.
	 *
	 * @param {{ identifier: string }} payload
	 * @return {Promise<MembershipLookupResult|undefined>}
	 */
	async lookupMembership({ commit }, { identifier } : { identifier: string }): Promise<MembershipLookupResult|undefined> {
		const axiosInstance: AxiosInstance = authState.axiosInst;
		const payload = {
			identifier,
			app8RestaurantId: authState.restaurant.app8_restaurant
		};
		try {
			const { data } = await axiosInstance.post<MembershipLookupResult>('/auth/get-user-membership-promo', payload);
			commit('SET_MEMBERSHIP_LOOKUP', data);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Apply the staff-selected provider's benefits to the order: set the member
	 * info, flag the matching member promos and recalculate the costs.
	 *
	 * @param {CheckoutMemberInfo} memberInfo
	 * @param {Menu[]} menus
	 * @return {Promise<void>}
	 */
	async applyPromoCode({ commit, dispatch }, { memberInfo, menus }: { memberInfo: CheckoutMemberInfo; menus: Menu[] }): Promise<void> {
		try {
			// We don't wanna populate the member object with an empty response
			if (memberInfo?.id) {
				commit('SET_MEMBER_INFO', memberInfo);
				commit('SET_MEMBER_PRICES', menus);
				await dispatch('setCosts');
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Apply the discount:
	 * - Promo code: Validate the discount code and get its value
	 * - Percentage by staff manually: Apply the discount percentage directly
	 *
	 * @param {{ discountCode?: string; percentage?: number }} payload
	 * @return {Promise<void>}
	 */
	async validateDiscount({ commit, dispatch }, { discountCode, percentage }: { discountCode?: string; percentage?: number }): Promise<void> {
		const axiosInstance: AxiosInstance = authState.axiosInst;
		try {
			if (discountCode) {
				const { data } = await axiosInstance.post('/orders/get-discount-info', { code: discountCode });
				commit('SET_DISCOUNT', data);
			}
			else if (Number.isInteger(percentage)) {
				commit('SET_DISCOUNT', { manual: true, value: percentage });
			}
			await dispatch('setCosts');
		}
		catch (error) {
			commit('SET_DISCOUNT', null);
			await dispatch('setCosts');
			handleAllErrors(error);
		}
	},

	/**
	 * Clear the applied discount and recalculate costs
	 *
	 * @return {Promise<void>}
	 */
	async clearDiscount({ commit, dispatch }): Promise<void> {
		try {
			commit('SET_DISCOUNT', null);
			await dispatch('setCosts');
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Get the estimated time and queue amount from pizzabot
	 *
	 * @return {Promise<void>}
	 */
	async fetchNextOrderQueueTime({ commit }): Promise<void> {
		try {
			const axiosInst: AxiosInstance = authState.axiosInst;
			const { data } = await axiosInst.get('/orders/get-next-order-queue-time');
			commit('SET_QUEUE', data);
		}
		catch (error) {
			throw error;
		}
	},

	/**
	 * Fire event with BEO number and seating details to POS
	 *
	 * @param {FireEventPayload} payload
	 * @return {Promise<void>}
	 */
	async fireEvent({}, payload: FireEventPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.post('/orders/fire-event', payload, {
				headers: {
					'Authorization': authState.idToken as string
				}
			});
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch the latest BEO number for a specific table
	 *
	 * @param {GetLatestBeoPayload} payload
	 * @return {Promise<string | undefined>}
	 */
	async fetchLatestBeoNumber({}, payload: GetLatestBeoPayload): Promise<string | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<string>('/orders/latest-beo', {
				params: payload
			});
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Submit the xlsx file to process repeating orders
	 *
	 * @param {{ file: File; startDate: string }} payload
	 * @return {Promise<void>}
	 */
	async submitRepeatingOrders({}, { file, startDate }: { file: File; startDate: string }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const formData = new FormData();
			formData.append('file', file);

			await axiosInst.post('/orders/repeat-orders-job', formData, {
				params: { start_date: startDate },
				headers: {
					'Content-Type': 'multipart/form-data',
					'Authorization': authState.idToken
				}
			});
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	setContactInformation({ commit }, contactInfo: Contact): void {
		commit('SET_CONTACT_INFORMATION', contactInfo);
	},

	setEventInformation({ commit }, eventInfo: EventInfo | null): void {
		commit('SET_EVENT_INFORMATION', eventInfo);
	},

	setTakeoutInformation({ commit }, takeoutInfo: Pickup): void {
		commit('SET_TAKEOUT_INFORMATION', takeoutInfo);
	},

	setDeliveryInformation({ commit }, deliveryInfo: DeliveryInfo): void {
		commit('SET_DELIVERY_INFORMATION', deliveryInfo);
	},

	setPaymentInformation({ commit }, paymentInfo: PaymentInfo): void {
		commit('SET_PAYMENT_INFORMATION', paymentInfo);
	},

	setQuoteInformation({ commit }, quoteInfo: QuoteInfo | null): void {
		commit('SET_QUOTE_INFORMATION', quoteInfo);
	},

	setCheckoutAnsweredQuestions({ commit }, answeredQuestions: AnsweredCustomQuestion[]): void {
		commit('SET_CHECKOUT_ANSWERED_QUESTIONS', answeredQuestions);
	},

	setCheckoutAutoAccept({ commit }, autoAccept: boolean): void {
		commit('SET_CHECKOUT_AUTO_ACCEPT', autoAccept);
	},

	setNotes({ commit }, notes: string): void {
		commit('SET_NOTES', notes);
	},

	setRestaurantCharges({ commit }, restaurant: Restaurant): void {
		commit('SET_RESTAURANT_CHARGES', restaurant);
	},

	async setModifiedFees({ commit, dispatch, state }, payload: { type: FeeType, updatedFee: CostsServiceCharge | CostsDeliveryCharge }): Promise<void> {
		const previousFee: CostsServiceCharge | CostsDeliveryCharge = { ...state.costs[payload.type] };
		commit('SET_MODIFIED_FEES', payload);
		try {
			await dispatch('setCosts');
		}
		catch (error) {
			// Revert the fee so a failed recalculation doesn't leave the modified fee in the costs state
			commit('SET_MODIFIED_FEES', { type: payload.type, updatedFee: previousFee });
			throw error;
		}
	},

	async getDeliveryFees({}, payload: { costs: Costs, checkout: Checkout }): Promise<CostsDeliveryCharge | void> {
		const axiosInstance: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInstance.post<CostsDeliveryCharge>('/orders/get-delivery-fee', payload);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	resetItemViewerState({ commit }): void {
		commit('RESET_ITEM_VIEWER_STATE');
	},

	resetOrderState({ commit, dispatch }): void {
		commit('RESET_ORDERS_STATE');
		dispatch('customQuestions/resetCustomFields', null, { root: true });
	},

	setItemSort({ commit }, sort: OrderItemSort): void {
		commit('SET_ITEM_SORT', sort);
	},

	setItemDiets({ commit }, diets: string[]): void {
		commit('SET_ITEM_DIETS', diets);
	},

	/**
	 * Add an open item to the cart via a single API call
	 *
	 * @param {{ item: MenuItem; quantity: number; saveOpenItem: boolean; }} payload
	 * @return {Promise<void>}
	 */
	async addOpenItemToCart({ commit, dispatch }, { item, quantity, saveOpenItem }: { item: MenuItem; quantity: number; saveOpenItem: boolean; }): Promise<void> {
		try {
			const orderItem: MenuOrderItem = await createOpenOrderItem(item, saveOpenItem);

			commit('SET_ORDER_ITEM', { selectedItem: orderItem, formatItem: false });
			commit('SET_ORDER_ITEM_QUANTITY', quantity);
			await dispatch('addToCart', '');
			commit('RESET_ITEM_VIEWER_STATE');
			await dispatch('menus/fetchMenus', {}, { root: true });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Delete an unsaved (ephemeral) open item that was removed from the order before submission.
	 * Errors are swallowed silently so a failed deletion never interrupts the ordering flow.
	 *
	 * @param {MenuOrderItem} item
	 * @return {Promise<void>}
	 */
	async deleteOpenItem({}, item: MenuOrderItem): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.delete(`/menus/open-items/${item.id}`, { params: { section_id: item.section_id ?? undefined } });
		}
		catch {
			// Swallowed: best-effort deletion, the item will be cleaned up by the sweep if this fails
		}
	},

	/**
	 * Add previously-ordered items to the cart, matched by SKU and Menu Type (Retail vs Event)
	 * to the live published menu. Checks started on the POS send their selected options as a flat list
	 * of skus rather than as orderOptions, so those are matched against the item's own option groups.
	 * All-or-nothing: if any item, or any option selected on it, is no longer available, nothing
	 * is added and the cart is left empty.
	 *
	 * @param {ActionContext} context - vuex action context
	 * @param {{ reorderItems: ReorderItem[]; publishedMenus: Menu[] }} payload - items to reorder + live menus
	 * @return {Promise<boolean>} true if all items were added, false if any item is no longer available. Throws if adding an item fails (e.g. network/server error).
	 */
	async addReorderItemsToCart({ commit, dispatch }, { reorderItems, publishedMenus }: { reorderItems: ReorderItem[]; publishedMenus: Menu[] }): Promise<boolean> {
		const isEventOrder = state.orderDetails.menuManagerEvent === true;

		const availableMenuItems: { item: MenuItem; sectionSlug: string }[] = publishedMenus
			.filter((menu: Menu) => menu.event_package === isEventOrder)
			.flatMap((menu: Menu) => menu.sections
				? menu.sections.flatMap((section: MenuSection) => (section.items || []).map((item: MenuItem) => ({ item, sectionSlug: section.slug })))
				: (menu.items || []).map((item: MenuItem) => ({ item, sectionSlug: '' })));

		const resolved: { orderOptions: OrderOptionGroup[]; match: { item: MenuItem; sectionSlug: string } }[] = [];
		for (const reorderItem of reorderItems) {
			const match = reorderItem.sku ? availableMenuItems.find(entry => entry.item.sku === reorderItem.sku) : undefined;
			if (!match) {
				return false;
			}

			let orderOptions: OrderOptionGroup[] = reorderItem.orderOptions || [];

			if (!orderOptions.length && reorderItem.items?.length) {
				const pending: Map<string, number> = new Map(reorderItem.items
					.filter((selectedOption: ReorderItem) => selectedOption.sku)
					.map((selectedOption: ReorderItem): [string, number] => [selectedOption.sku as string, selectedOption.quantity || 1]));

				orderOptions = selectPosCheckOptionGroups(formatOrderItem(match.item).orderOptions || [], pending);
				if (pending.size) {
					return false;
				}
			}

			resolved.push({ orderOptions, match });
		}

		try {
			for (const { orderOptions, match } of resolved) {
				dispatch('setSelectedItem', { selectedItem: match.item, sectionSlug: match.sectionSlug });
				dispatch('setOrderItemOptions', orderOptions);
				dispatch('setOrderItemQuantity', 1);
				await dispatch('addToCart', '');
			}
		}
		catch (error) {
			commit('CLEAR_CART');
			commit('RESET_ITEM_VIEWER_STATE');
			throw error;
		}

		commit('RESET_ITEM_VIEWER_STATE');
		return true;
	},

	async applyItemDiscount({ commit, dispatch }, { item, percentageValue }: { item: MenuOrderItem, percentageValue: number | null }): Promise<void> {
		const previousDiscount: CostsDiscount | null = item.discount ?? null;
		const discount: CostsDiscount | null = percentageValue ? {
			percentageValue,
			code: i18n.t('orders.item_discount', { percentage: percentageValue }),
			amount: 0,
			type: 'percentage',
			manual: true
		} : null;
		commit('SET_ITEM_DISCOUNT', { item, discount });
		try {
			await dispatch('setCosts');
			commit('MERGE_MATCHING_CART_ITEM', item);
		}
		catch (error) {
			commit('SET_ITEM_DISCOUNT', { item, discount: previousDiscount });
			handleAllErrors(error);
		}
	}
};